import {
  cancelDownload,
  writeDownloadedChunksToDisk,
  updateTimeStatusOfDownloadingFileCommon,
  resumeDownload,
  pausedDownload,
  pauseDownload,
} from "/WebInterface/new-ui/modules/advanced-download/common.js";
import downloadingQueue from "/WebInterface/new-ui/modules/advanced-download/downloading-queue.js";
import store from "/WebInterface/new-ui/store/index.js";
import C from "/WebInterface/new-ui/assets/js/utils/log.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import {
  isFileOpen,
  closeFile,
} from "/WebInterface/new-ui/upload/utils/apis.js";
import { ajax } from "/WebInterface/new-ui/assets/js/utils/ajax.js";

/**
 * File Downloading Activities Module
 * @constructor
 * @param {array} queue - Maintains the queue of files to download
 * @param {integer} maxRequestPerDomain - Max API requests we can hit simultaneously while downloading file
 * @param {object} streamingFile - Current downloading file
 * @param {integer} chunkWrittenToTheDisk - Number of the chunk written in the local disk
 * @param {integer} bytesDownloaded - Total bytes downloaded
 * @param {integer} bytesWritten - Total bytes written in the local disk
 * @param {object} chunksRemainsToWrite - Maintains the unordered downloaded chunks that are waiting to write in the local disk
 * @param {array} chunks - Maintains chunks of the current downloading file
 * @param {integer} elapsedTime - Elapsed time of currently downloading file in seconds
 * @param {integer} lastBytesDownloaded - Number of bytes downloaded in the last second (For speed calculation).
 * @param {boolean} isPause - Keep status of pause of the current downloading file.
 * @param {integer} maxRetries - Max retries allows to the current downloading file before transferring to failed status.
 * @param {boolean} fileDownloadingInProgress - Keep status of downloading process.
 */

class WSAdvanceDownload {
  constructor({
    maxRequest = 10,
    showServerReadProgress = false,
    testRecovery = false,
  }) {
    this.showServerReadProgress = showServerReadProgress;
    this.testRecovery = testRecovery;
    C.log("WS Download initialized with:", {
      maxRequest,
      showServerReadProgress,
      testRecovery,
    });
    this.streamingFile = null;
    this.lastWrittenChunkIndex = 0;
    this.chunkWrittenToTheDisk = 1;
    this.bytesDownloaded = 0;
    this.bytesWritten = 0;
    this.chunks = [];
    this.chunksDownloaded = {};
    this.chunksRemainsToWrite = {};
    this.chunksBeingWritten = {};
    this.chunksWritten = {};
    this.elapsedTime = 0;
    this.lastBytesDownloaded = 0;
    this.lastBytesDownloadedInTenSec = 0;
    this.isPause = false;
    this.maxRetries = 2;
    this.maxRequestPerDomain = 3;
    this.maxRequest = maxRequest + 1; //As 1 additional socket is kept just for ACKs
    this.retries = 0;
    this.retriedChunks = [];
    this.lastIndexOfChunkWrittenToDisk = 0;
    this.lastMessageTime = null;
    this.fileDownloaded = false;
    this.lastReceivedChunkIndex = 0;
    this.EOFChunkIndex = null;
    this.lastUpdateTime = null;
    this.lastRandomCloseTime = Date.now();
    this.randomCloseInterval = 10000;
  }

  // Reset required vars on new file download
  resetVars = () => {
    this.sockets = [];
    this.chunkNum = 0;
    this.canceled = false;
    this.fileClosed = false;
    this.isPause = false;
    this.compressingTimeoutId = null;
    this.pendingChunks = [];
    this.lastIndexOfChunkWrittenToDisk = 0;
    this.lastWrittenChunkIndex = 0;
    this.fileDownloaded = false;
    this.chunksDownloaded = {};
    this.chunksRemainsToWrite = {};
    this.chunksBeingWritten = {};
    this.chunksWritten = {};
    this.lastReceivedChunkIndex = 0;
    this.EOFChunkIndex = null;

    store.dispatch("stopAliveSession");
  };

  logNoMessage = () => {
    if (this.lastMessageTime === null) {
      return;
    }

    const timeElapsed = Date.now() - this.lastMessageTime;
    if (timeElapsed > 10000) {
      C.error(
        `No message received from server for ${timeElapsed / 1000} seconds`
      );

      const chunksWrtigin = Object.keys(this.chunksBeingWritten).filter(
        (key) =>
          !Object.prototype.hasOwnProperty.call(this.chunksBeingWritten, key)
      );
      const chunksYetToWrite = Object.keys(this.chunksRemainsToWrite).filter(
        (key) =>
          !Object.prototype.hasOwnProperty.call(this.chunksRemainsToWrite, key)
      );
      C.warn(
        `Chunks being written: ${chunksWrtigin.length} - ${chunksWrtigin}. Chunk yet to write: ${chunksYetToWrite.length} - ${chunksYetToWrite}. Last received chunk: ${this.lastReceivedChunkIndex}. Last written chunk: ${this.lastWrittenChunkIndex}.`
      );
      this.lastMessageTime = Date.now();
    }
  };

  removeSocketFromStorage = (socket) => {
    if (socket.readyState === WebSocket.OPEN) {
      socket.close();
      return;
    }
    if (!socket) return;
    if (socket && socket.removeEventListener) {
      const socketEvents = Object.getOwnPropertyNames(socket);
      for (const event of socketEvents) {
        if (event.startsWith("on") && typeof socket[event] === "function") {
          socket.removeEventListener(event.slice(2), socket[event]);
        }
      }
    }
    const index = this.sockets.indexOf(socket);
    if (index !== -1) {
      this.sockets.splice(index, 1);
    }
  };

  getPercentage = (fileSize, bytesReceived) => {
    if (fileSize === 0) {
      return 0;
    }
    const progress = Math.floor((bytesReceived / fileSize) * 100);
    return progress;
  };

  // create new socket for downloading file :- runs when file starts downloading
  createSocket = () => {
    const { size: fileSize, id: fileId } = this.streamingFile;
    const transferId = this.transferId;
    const { hostname, port, protocol } = location;
    const ws_protocol = "ws" + (protocol.startsWith("https") ? "s" : "");
    const sockets = this.sockets;

    const socket = new WebSocket(
      `${ws_protocol}:/${hostname}:${port}/DOWNLOAD/${transferId}`
    );
    socket.binaryType = "arraybuffer";
    socket.chunksBeingSent = []; // Initialize chunksBeingSent array
    socket.index = sockets.length; // Add socket index

    sockets.push(socket);
    // socket on open
    socket.onopen = () => {
      // sockets.push(socket);
      if (!this.ackScoket) {
        C.log(`Download: Setting ack socket index: ${socket.index}`);
        this.ackScoket = socket;
        this.ackScoket.isACKSocket = true;
        const bufferToSent = new ArrayBuffer(4);
        const indexView = new DataView(bufferToSent);
        indexView.setUint32(0, 0);
        this.ackScoket.send(bufferToSent);
        this.ackScoket.sentAcks = this.ackScoket.sentAcks || 0;
        this.ackScoket.sentAcks++;
        C.log(`Download: Sent ack of chunk 0 to socket: ${socket.index}`);
      }
    };

    // socket on close
    socket.onclose = (event) => {
      // C.log("Socket Closed", event);
      C.warn("Socket Closed");
      this.removeSocketFromStorage(socket);
      this.handleSocketConnectionError(socket);
    };

    // socket on error
    socket.onerror = (event) => {
      C.warn("Socket Closed");
      this.removeSocketFromStorage(socket);
      this.handleSocketConnectionError(socket);
    };

    // socket on message (get response on send)
    socket.onmessage = (event) => {
      this.lastMessageTime = Date.now();
      if (typeof event.data === "string") {
        C.log(`Download: Received: ${event.data}`);
        this.closeFileWithError(event.data);
        return;
      }

      if (
        !this.ackScoket ||
        (this.ackScoket && this.ackScoket.readyState !== WebSocket.OPEN)
      ) {
        C.log(
          `Download: No ACK Socket set, Setting ack socket index: ${socket.index}`
        );
        this.ackScoket = socket;
        this.ackScoket.isACKSocket = true;
      }

      const buffer = event.data;
      const view = new DataView(buffer);
      const bytesWritten = this.bytesWritten;
      const index = view.getUint32(0);
      const contentLength = view.getUint32(4);
      const chunk = event.data.slice(8);

      C.log(`Download: Received: ${index}, Size: ${parseInt(contentLength)}`);

      //  Send chunk Index to BE
      const bufferToSent = new ArrayBuffer(4);
      const indexView = new DataView(bufferToSent);
      let receivedLastChunk = false;
      indexView.setUint32(0, index);
      if (this.ackScoket) {
        this.ackScoket.sentAcks = this.ackScoket.sentAcks || 0;
        this.ackScoket.sentAcks++;
        this.ackScoket.send(bufferToSent);
      }
      socket.chunksBeingSent.push(index); // Track chunk being sent
      C.log(`Download: Ack: ${index}`);
      this.lastReceivedChunkIndex = index;
      if (this.chunksDownloaded[index] === undefined) {
        this.chunksDownloaded[index] = index;
        const bytesDownloaded = (this.bytesDownloaded +=
          parseInt(contentLength));

        C.log(
          `Download: ${bytesDownloaded}/${fileSize} (${this.getPercentage(
            fileSize,
            bytesDownloaded
          )}%)`
        );

        if (parseInt(contentLength) === 0) {
          this.EOFChunkIndex = index;
          C.log(
            `Download: Received last chunk, 0 size chunk ${index}, ignored`
          );
        } else {
          this.chunksRemainsToWrite[index] = {
            data: chunk,
            size: parseInt(contentLength),
          };
        }
        // dispatch to update downloading file card UI progress bar
        if (contentLength) {
          store.dispatch("updateProgressBarDownload", {
            fileId,
            bytesDownloaded,
            bytesWritten,
            fileSize,
          });

          // Update server read progress if enabled
          if (this.showServerReadProgress) {
            const serverYetToRead =
              (this.streamingFile.size - bytesDownloaded) / (1024 * 1024); // Convert to MB
            C.log(
              `Updating server read progress: ${serverYetToRead.toFixed(
                2
              )} MB remaining`
            );
            this.updateServerReadProgress(serverYetToRead);
          }
        }

        this.checkIfFileDownloaded();
        this.writeChunksToDisk();
      } else {
        C.log(`Download: ${index} exists, ignored`);
        this.writeChunksToDisk();
      }
    };
  };

  checkIfFileDownloaded = () => {
    const { id: fileId } = this.streamingFile;
    const allChunks = this.chunkNosFromList(this.chunksDownloaded);
    const writtenChunks = this.chunkNosFromList(this.chunksWritten);
    const missingChunks = this.findRemainingChunks(allChunks);

    C.log(
      `Download: All Chunks length: ${allChunks.length}, Written Chunks length: ${writtenChunks.length}, Missing Chunks length: ${missingChunks.length}, EOFChunkIndex: ${this.EOFChunkIndex}`
    );

    if (this.EOFChunkIndex !== null && missingChunks.length > 0) {
      C.warn(
        `Download: Received last chunk, but missing chunks: ${missingChunks.join(
          ", "
        )}. So waiting for missing chunks. Chunks length ${
          allChunks.length
        }, written chunks: ${writtenChunks.length}, missing chunks: ${
          missingChunks.length
        }`
      );
    }
    this.fileDownloaded =
      this.EOFChunkIndex !== null &&
      missingChunks.length === 0 &&
      allChunks.length - 1 === writtenChunks.length &&
      allChunks.length >= this.EOFChunkIndex;
    if (this.fileDownloaded) {
      C.success(
        `Download: Completed, writing to disk may take time, once all chunks are written it will start finalization`
      );
      if (this.compressingTimeoutId) return;

      store.dispatch("keepSessionAlive");

      this.compressingTimeoutId = setTimeout(() => {
        store.dispatch("updateFileStatusDownload", {
          fileId,
          updates: {
            status: "compressing",
          },
        });
      }, 3000);
      this.fileClosed = true;
      this.closeAllSockets();
    }
  };

  // handles socket connection error
  handleSocketConnectionError = async (socket) => {
    if (socket && socket.readyState === WebSocket.OPEN) return;
    if (this.fileClosed || this.isPause) return;
    const response = await isFileOpen({
      transfer_type: "download",
      download_id: this.transferId,
      random: Math.random(),
    }).catch((ex) => {
      if (this.fileClosed || this.isPause) {
        C.error("Download: File is downloaded or canceled, ignoreing isOpen");
        return;
      }
      this.closeFileWithError("failed");
      C.error("Download: isOpen for download failed, canceling download");
      return;
    });
    if (
      response === "CLOSED" ||
      response?.toLowerCase().indexOf("failure") >= 0 ||
      response?.toLowerCase().indexOf("error") >= 0
    ) {
      if (this.fileClosed || this.isPause) {
        C.error("Download: File is downloaded or canceled, ignoreing isOpen");
        return;
      }
      this.closeFileWithError("failed");
      C.error("Download: isOpen for download failed, canceling download");
      return;
    }
    if (!this.fileClosed && !this.isPause) {
      this.recreateSocketOnError();
    }
  };

  startDownloading = () => {
    if (this.fileClosed) return;
    clearInterval(this.noMsgIntervalId);
    this.noMsgIntervalId = setInterval(this.logNoMessage, 5000);
    const openSockets = this.sockets.filter(
      (socket) =>
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
    );
    if (openSockets.length < this.maxRequest) {
      for (let x = openSockets.length; x < this.maxRequest; x++) {
        this.createSocket();
      }
    }
    downloadingQueue.handleUpdateDownloadingStatus(true);
    this.updateTimeStatusOfDownloadingFile();

    if (this.testRecovery) {
      this.randomlyCloseSocket();
    }
  };

  recreateSocketOnError = () => {
    if (this.fileClosed || this.isPause) return;
    C.log(
      `Download: Recreating sockets: ${this.maxRequest - this.sockets.length}`
    );
    const openSockets = this.sockets.filter(
      (socket) =>
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
    );
    if (openSockets.length < this.maxRequest) {
      for (let x = openSockets.length; x < this.maxRequest; x++) {
        this.createSocket();
      }
    }
  };

  // Check next file in the queue and will start downloading the next file
  nextFileInQueue(canceled) {
    downloadingQueue.nextFileInDownloadingQueue(canceled);
    // nextFileInDownloadingQueue(this, canceled);
  }

  findRemainingChunks = (numbers) => {
    const missingNumbers = [];
    // Iterate through the array starting from the second element
    for (let i = 1; i < numbers.length; i++) {
      // Calculate the difference between consecutive numbers
      const diff = numbers[i] - numbers[i - 1];
      // If the difference is greater than 1, there is a gap
      if (diff > 1) {
        // Add the missing numbers to the missingNumbers array
        for (let j = numbers[i - 1] + 1; j < numbers[i]; j++) {
          missingNumbers.push(j);
        }
      }
    }
    return missingNumbers;
  };

  chunkNosFromList = (list) => {
    const _list = [];
    for (const key in list) {
      if (list.hasOwnProperty(key)) {
        _list.push(parseInt(key));
      }
    }
    return _list.sort((a, b) => a - b);
  };

  // Calculate speed and time remaining to download the file
  updateTimeStatusOfDownloadingFile = () => {
    updateTimeStatusOfDownloadingFileCommon(this);
  };

  // Initiate downloading of the file
  startFileDownloading = async (file) => {
    const initDownload = async () => {
      this.resetVars();

      // Reset all required variables
      this.streamingFile = file;
      this.chunkWrittenToTheDisk = 1;
      this.bytesDownloaded = 0;
      this.bytesWritten = 0;
      this.chunksDownloaded = {};
      this.chunksRemainsToWrite = {};
      this.chunksBeingWritten = {};
      this.chunksWritten = {};
      this.chunks = [];
      this.elapsedTime = 0;
      this.lastBytesDownloaded = 0;
      this.lastBytesDownloadedInTenSec = 0;
      this.isPause = false;
      this.retrying = false;
      this.retries = 0;
      this.lastChunkIndex = null;
      this.EOFChunkIndex = null;

      const { id: fileId } = file;

      const c2f = utils.cookie.get("currentAuth");

      // Provides unique id to download the file
      try {
        const resp = await ajax.call({
          timeout: 10000000,
          data: {
            command: `download`,
            transfer_type: "download",
            path: file.href_path,
            c2f,
          },
        });

        if (resp) {
          const xml = new DOMParser().parseFromString(resp, "text/xml");
          const result = utils.xmlToJson(xml);

          this.transferId = result?.commandResult?.response;

          if (this.fileClosed) {
            return;
          }

          this.writableStream = await file.fileHandle.createWritable();

          store.dispatch("updateFileStatusDownload", {
            fileId,
            updates: {
              status: "downloading",
            },
          });

          C.success(
            `Download: File download started. Transfer ID: ${this.transferId}, File ID: ${fileId}, Path: ${file.href_path}`
          );

          // Start downloading chunks
          this.startDownloading();
        }
      } catch (error) {
        if (this.fileClosed) {
          return;
        }

        this.cancel();
      } finally {
        if (this.fileClosed) {
          await closeFile({
            transfer_type: "download",
            download_id: this.transferId,
            random: Math.random(),
          });

          const { id: fileId } = file;
          store.dispatch("updateFileStatusDownload", {
            fileId,
            updates: {
              status: "canceled",
              downloadingError: "canceled",
            },
          });

          this.nextFileInQueue(true);
        }
      }
    };

    const checkFileAndStartDownload = async () => {
      try {
        const fileStatus = await isFileOpen({
          transfer_type: "download",
          random: Math.random(),
          path: file.href_path,
        });

        if (this.fileClosed) {
          return;
        }

        C.log(`Download: isOpen returned: ${fileStatus}`);

        if (
          fileStatus !== "CLOSED" &&
          fileStatus.toLowerCase().indexOf("error") < 0 &&
          fileStatus.toLowerCase().indexOf("failure") < 0
        ) {
          setTimeout(() => {
            checkFileAndStartDownload();
          }, 5000);
          return;
        }

        initDownload();
      } catch (error) {
        if (this.fileClosed) {
          return;
        }

        setTimeout(() => {
          checkFileAndStartDownload();
        }, 5000);
      } finally {
        if (this.fileClosed) {
          const { id: fileId } = file;
          store.dispatch("updateFileStatusDownload", {
            fileId,
            updates: {
              status: "canceled",
              downloadingError: "canceled",
            },
          });

          this.nextFileInQueue(true);
        }
      }
    };

    checkFileAndStartDownload();
  };

  closeFileDownloading = async () => {
    const file = this.streamingFile;
    const { id: fileId } = file;
    this.closeFileInProgress = true;
    const that = this;
    try {
      // Close writable stream
      C.success(`File downloaded, finalizing now`);

      const allChunks = that.chunkNosFromList(this.chunksDownloaded);
      C.log(`Download: All Received Chunks: ${allChunks.join(", ")}`);

      const missingChunks = that.findRemainingChunks(allChunks);

      if (missingChunks.length > 0) {
        C.error(
          `Download: Missing Chunks: ${missingChunks.join(
            ", "
          )}. Chunks being written: ${Object.keys(that.chunksBeingWritten)
            ?.sort((a, b) => a - b)
            .join(", ")}. Chunks yet to write: ${Object.keys(
            that.chunksRemainsToWrite
          )
            ?.sort((a, b) => a - b)
            .join(", ")}`
        );
      }
      await closeFile({
        transfer_type: "download",
        download_id: this.transferId,
        random: Math.random(),
      });
      C.log(`Download: CloseFile called as download completed`);
      C.time("Copy to disk");
      try {
        await this.writableStream.close();
      } catch (ex) {}
      C.timeEnd("Copy to disk");
      C.success(`File written completely`);

      // Only remove progress panel when both download and write are complete
      const progressDiv = document.getElementById(
        "server-read-progress-download"
      );
      // if (
      //   progressDiv &&
      //   this.bytesWritten >= file.size &&
      //   !this.closeFileInProgress
      // ) {
      //   progressDiv.remove();
      // }

      if (file.storageMethod === "sandbox") {
        store.dispatch("updateFileStatusDownload", {
          fileId,
          updates: {
            status: "sandboxDownloading",
          },
        });

        setTimeout(() => {
          store.dispatch("updateFileStatusDownload", {
            fileId,
            updates: {
              status: "downloaded",
              timeTakenToComplete: this.elapsedTime
                ? this.elapsedTime * 1000
                : 200,
            },
          });
        }, 10000);
      } else {
        store.dispatch("updateFileStatusDownload", {
          fileId,
          updates: {
            status: "downloaded",
            timeTakenToComplete: this.elapsedTime
              ? this.elapsedTime * 1000
              : 200,
          },
        });
      }
      clearTimeout(this.compressingTimeoutId);
      this.nextFileInQueue();
    } catch (error) {
      C.log(error);
    } finally {
      store.dispatch("stopAliveSession");
      clearInterval(this.elapsedIntervalId);
      clearInterval(this.intervalIdAverageTenSecondSpeed);
      clearInterval(this.noMsgIntervalId);
    }
    const progressDiv = document.getElementById(
      "server-read-progress-download"
    );
    if (progressDiv) {
      progressDiv.remove();
    }
    this.closeFileInProgress = false;
  };

  // Writes chunks in the disk using the file handle (FileSystemAPI :- FileHandle)
  writeChunksToDisk = async () => {
    writeDownloadedChunksToDisk(this);
  };

  // close all connected sockets, uses when download completes or canceled
  closeAllSockets = () => {
    clearInterval(this.noMsgIntervalId);
    clearInterval(this.elapsedIntervalId);
    clearInterval(this.intervalIdAverageTenSecondSpeed);
    clearInterval(this.chunks_status_interval);

    // Remove server read progress UI
    // const progressDiv = document.getElementById(
    //   "server-read-progress-download"
    // );
    // if (progressDiv) {
    //   progressDiv.remove();
    // }

    this.sockets.forEach((socket) => {
      socket.close();
    });
  };

  // Cancel downloading file
  cancel = async () => {
    if (this.fileClosed) return;
    this.fileClosed = true;

    if (!this.transferId) {
      return;
    }

    const beforeCancelingFile = () => {
      clearInterval(this.noMsgIntervalId);
      clearInterval(this.elapsedIntervalId);
      clearInterval(this.intervalIdAverageTenSecondSpeed);
      clearInterval(this.chunks_status_interval);

      this.canceled = true;
      this.fileClosed = true;
      this.closeAllSockets();
    };
    C.log(`Download: CloseFile called as download canceled`);
    const afterCancelingFile = async () => {
      await closeFile({
        transfer_type: "download",
        download_id: this.transferId,
        random: Math.random(),
      });
      this.nextFileInQueue(true);
    };

    cancelDownload({
      streamingFile: this.streamingFile,
      writableStream: this.writableStream,
      beforeCancelingFile,
      afterCancelingFile,
    });
  };

  // Update downloading status globally in the project
  handleUpdateDownloadingStatus = (downloading) => {
    if (
      (downloading === true || downloading === false) &&
      downloading !== this.fileDownloadingInProgress
    ) {
      this.fileDownloadingInProgress = downloading;

      store.dispatch("setChunkProcessingStatus", {
        type: "isDownloading",
        status: this.fileDownloadingInProgress,
      });
    }

    return this.fileDownloadingInProgress;
  };

  closeFileWithError = async (errorMsg) => {
    const file = this.streamingFile;
    const { id: fileId } = file;
    await closeFile({
      transfer_type: "download",
      download_id: this.transferId,
      random: Math.random(),
    });
    C.log(`Download: CloseFile called as download failed`);
    clearInterval(this.elapsedIntervalId);
    clearInterval(this.intervalIdAverageTenSecondSpeed);
    clearInterval(this.chunks_status_interval);

    this.canceled = true;
    this.fileClosed = true;
    this.closeAllSockets();

    try {
      await this.writableStream.close();
      await file.fileHandle.remove();
    } catch (error) {
      C.log(error);
    } finally {
      store.dispatch("updateFileStatusDownload", {
        fileId,
        updates: {
          status: "failed",
          downloadingError:
            "File failed please retry to download file: " + errorMsg,
        },
      });

      this.nextFileInQueue(true);
    }
  };

  remove = (fileId) => {
    if (!fileId) return;
    this.queue = this.queue.filter(({ id }) => id !== fileId);
    store.dispatch("deleteFileInDownload", { id: fileId });
  };

  // pauses downloading file
  pause = () => {
    const onPause = () => {
      clearInterval(this.chunks_status_interval);
    };
    pauseDownload(this, onPause);
    this.closeAllSockets();
    pausedDownload(this);
  };

  resume = () => {
    resumeDownload(this, this.startDownloading);
  };

  updateServerReadProgress = (serverYetToRead) => {
    if (this.isPause || this.fileClosed) {
      C.log("Not updating progress - paused or closed");
      return;
    }

    // Throttle updates to once every 500ms
    if (this.lastUpdateTime && Date.now() - this.lastUpdateTime < 500) {
      return;
    }
    this.lastUpdateTime = Date.now();

    const globalDownloads = document.getElementById("globalDownloads");
    if (!globalDownloads) {
      C.log("Global downloads div not found");
      return;
    }

    let progressDiv = document.getElementById("server-read-progress-download");
    if (!progressDiv) return;

    progressDiv.style.display = "block";

    this.updateRecoveryMessage(progressDiv);
    this.updateProgressBar(progressDiv, serverYetToRead);
    this.updateSocketTable(progressDiv);
  };

  updateRecoveryMessage = (progressDiv) => {
    let recoveryMessage = progressDiv.querySelector(".recovery-message");
    if (!recoveryMessage) {
      recoveryMessage = document.createElement("div");
      recoveryMessage.className = "recovery-message";
      Object.assign(recoveryMessage.style, {
        position: "absolute",
        top: "5px",
        right: "5px",
        backgroundColor: "rgba(255, 255, 0, 0.7)",
        padding: "2px 5px",
        borderRadius: "3px",
        fontSize: "12px",
        fontWeight: "bold",
      });
      progressDiv.appendChild(recoveryMessage);
    }

    const isRecovering = this.sockets.some((socket) => socket.isStuck);
    recoveryMessage.textContent = isRecovering ? "Recovery in progress..." : "";
    recoveryMessage.style.display = isRecovering ? "block" : "none";
  };

  updateProgressBar = (progressDiv, serverYetToRead) => {
    const progressText = progressDiv.querySelector(".progress-text");
    const { size: fileSize } = this.streamingFile;

    // Calculate percentages for received and written bytes
    const receivedPercentage = (this.bytesDownloaded / fileSize) * 100;
    const writtenPercentage = (this.bytesWritten / fileSize) * 100;

    // Create container for both progress bars
    let progressBarsContainer = progressDiv.querySelector(
      ".progress-bars-container"
    );
    if (!progressBarsContainer) {
      progressBarsContainer = document.createElement("div");
      progressBarsContainer.className = "progress-bars-container";
      progressBarsContainer.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 5px;
        margin-bottom: 5px;
      `;
      // Remove the old progress bar container
      const oldProgressBar = progressDiv.querySelector(".progress-bar");
      if (oldProgressBar) {
        oldProgressBar.remove();
      }
      progressDiv.insertBefore(progressBarsContainer, progressText);
    }

    // Create or get the download progress bar container
    let downloadContainer = progressBarsContainer.querySelector(
      ".download-container"
    );
    if (!downloadContainer) {
      downloadContainer = document.createElement("div");
      downloadContainer.className = "download-container";
      downloadContainer.style.cssText = `
        width: 100%;
        height: 5px;
        background-color: var(--bg-primary-progress);
        border-radius: 10px;
        overflow: hidden;
        position: relative;
      `;
      progressBarsContainer.appendChild(downloadContainer);
    }

    // Create or get the write progress bar container
    let writeContainer =
      progressBarsContainer.querySelector(".write-container");
    if (!writeContainer) {
      writeContainer = document.createElement("div");
      writeContainer.className = "write-container";
      writeContainer.style.cssText = `
        width: 100%;
        height: 5px;
        background-color: var(--bg-primary-progress);
        border-radius: 10px;
        overflow: hidden;
        position: relative;
      `;
      progressBarsContainer.appendChild(writeContainer);
    }

    // Create or get the download progress bar
    let downloadBar = downloadContainer.querySelector(
      ".download-progress-fill"
    );
    if (!downloadBar) {
      downloadBar = document.createElement("div");
      downloadBar.className = "download-progress-fill";
      downloadBar.style.cssText = `
        height: 100%;
        background-color: #90EE90;
        transition: width 0.3s ease;
        position: absolute;
        top: 0;
        left: 0;
      `;
      downloadContainer.appendChild(downloadBar);
    }
    downloadBar.style.width = `${receivedPercentage}%`;

    // Create or get the write progress bar
    let writeBar = writeContainer.querySelector(".write-progress-fill");
    if (!writeBar) {
      writeBar = document.createElement("div");
      writeBar.className = "write-progress-fill";
      writeBar.style.cssText = `
        height: 100%;
        background-color: #228B22;
        transition: width 0.3s ease;
        position: absolute;
        top: 0;
        left: 0;
      `;
      writeContainer.appendChild(writeBar);
    }
    writeBar.style.width = `${writtenPercentage}%`;

    // Update text to show both received and written progress
    progressText.textContent = `Downloaded: ${(
      this.bytesDownloaded /
      (1024 * 1024)
    ).toFixed(2)} MB / ${(fileSize / (1024 * 1024)).toFixed(
      2
    )} MB | Written: ${(this.bytesWritten / (1024 * 1024)).toFixed(2)} MB`;
  };

  updateSocketTable = (progressDiv) => {
    let socketTable = progressDiv.querySelector(".socket-info-table");
    if (!socketTable) {
      socketTable = document.createElement("table");
      socketTable.className = "socket-info-table";
      Object.assign(socketTable.style, {
        width: "100%",
        marginTop: "10px",
        borderCollapse: "collapse",
      });
      progressDiv.appendChild(socketTable);
    }

    let [indexRow, bufferRow] = this.getOrCreateRows(socketTable);
    this.updateSocketRows(indexRow, bufferRow);
    this.updateStatusRow(socketTable);
    this.updateSummaryRow(socketTable);
  };

  getOrCreateRows = (table) => {
    const createRow = (className) => {
      const row = table.querySelector(`.${className}`) || table.insertRow();
      row.className = className;
      return row;
    };
    return [createRow("index-row"), createRow("buffer-row")];
  };

  updateSocketRows = (indexRow, bufferRow) => {
    indexRow.innerHTML =
      "<td style='width: 80px; text-align: left;' class='text-primaryMain'>Socket:</td>";
    bufferRow.innerHTML =
      "<td style='width: 80px; text-align: left;' class='text-primaryMain'>Ackd:</td>";

    const cellWidth = `${100 / (this.sockets.length + 1)}%`;

    this.sockets.forEach((socket, index) => {
      const chunksInBuffer = socket.chunksBeingSent?.length || 0;
      //if socket is closed, show index in red
      this.createCell(
        indexRow,
        cellWidth,
        `<strong style='color: ${
          socket.readyState === WebSocket.CLOSED ? "red" : "inherit"
        }'>${index + 1} ${socket.isACKSocket ? "<sup>*</sup>" : ""}</strong>`
      );
      this.createCell(
        bufferRow,
        cellWidth,
        `${chunksInBuffer} ${socket.isACKSocket ? `(${socket.sentAcks})` : ""}`
      );
    });

    // Add total column
    const totalBufferInMB = (
      this.sockets.reduce(
        (total, socket) => total + (socket.bufferedAmount || 0),
        0
      ) /
      (1024 * 1024)
    ).toFixed(2);
    const totalChunksInBuffer = this.sockets.reduce(
      (total, socket) => total + (socket.chunksBeingSent?.length || 0),
      0
    );

    this.createCell(indexRow, cellWidth, "<strong>T</strong>", true);
    this.createCell(bufferRow, cellWidth, `${totalChunksInBuffer}`, true);
  };

  createCell = (row, width, content, isTotal = false) => {
    const cell = row.insertCell();
    Object.assign(cell.style, {
      width,
      textAlign: "center",
      fontWeight: isTotal ? "bold" : "normal",
    });
    cell.innerHTML = content;
  };

  updateStatusRow = (socketTable) => {
    const chunksReadyToWrite = Object.keys(this.chunksRemainsToWrite).length;
    let statusRow =
      socketTable.querySelector(".status-row") || socketTable.insertRow();
    statusRow.className = "status-row";
    statusRow.innerHTML = `<td colspan="${
      this.sockets.length + 2
    }" style='text-align: left;'>Chunks ready to write: ${chunksReadyToWrite}</td>`;
  };

  updateSummaryRow = (socketTable) => {
    if (this.isPause || this.fileClosed) return;

    const summary = {
      totalChunksReceived: Object.keys(this.chunksDownloaded).length,
      chunksWritten: Object.keys(this.chunksWritten).length,
      chunksBeingWritten: Object.keys(this.chunksBeingWritten).length,
      chunksToWrite: Object.keys(this.chunksRemainsToWrite).length,
    };

    let summaryRow =
      socketTable.querySelector(".summary-row") || socketTable.insertRow();
    summaryRow.className = "summary-row";
    summaryRow.innerHTML = `
      <td colspan="${this.sockets.length + 2}" style='text-align: left;'>
        Total chunks received: ${summary.totalChunksReceived} |
        Written: ${summary.chunksWritten} |
        Writing: ${summary.chunksBeingWritten} |
        To write: ${summary.chunksToWrite}
      </td>
    `;
  };

  randomlyCloseSocket = () => {
    C.log(
      `Download: Recovery: Test recovery check - testRecovery: ${this.testRecovery}, isPause: ${this.isPause}, fileClosed: ${this.fileClosed}, fileDownloaded: ${this.fileDownloaded}`
    );
    if (
      !this.testRecovery ||
      this.isPause ||
      this.fileClosed ||
      this.fileDownloaded
    ) {
      C.log(
        `Download: Recovery: Skipping random socket close because: ${
          !this.testRecovery
            ? "test recovery disabled"
            : this.isPause
            ? "download is paused"
            : this.fileClosed
            ? "file is closed"
            : "file is downloaded"
        }`
      );
      return;
    }

    setTimeout(() => {
      this.randomlyCloseSocket();
    }, 10000);

    const now = Date.now();
    const timeSinceLastClose = now - this.lastRandomCloseTime;
    C.log(
      `Download: Recovery: Time since last random close: ${
        timeSinceLastClose / 1000
      }s (minimum interval: ${this.randomCloseInterval / 1000}s)`
    );

    if (timeSinceLastClose < this.randomCloseInterval) {
      C.log(
        `Download: Recovery: Skipping random socket close - too soon since last close`
      );
      return;
    }

    const randomValue = Math.random();
    C.log(
      `Download: Recovery: Random value: ${randomValue.toFixed(
        2
      )} (threshold: 0.1)`
    );

    if (randomValue < 0.3) {
      const openSockets = this.sockets.filter(
        (socket) =>
          (socket.readyState === WebSocket.OPEN ||
            socket.readyState === WebSocket.CONNECTING) &&
          socket.chunksBeingSent?.length > 0
      );

      C.log(
        `Download: Found ${openSockets.length} eligible sockets for closing (${this.sockets.length} total sockets)`
      );

      if (openSockets.length > 0) {
        C.warn(
          `Download: Randomly closing ${
            openSockets.length
          } active sockets for testing recovery. Socket indices: ${openSockets
            .map((s) => s.index)
            .join(", ")}`
        );
        this.lastRandomCloseTime = now;
        openSockets.forEach((socket) => {
          C.log(
            `Download: Closing socket ${socket.index} with ${socket.chunksBeingSent?.length} chunks being sent`
          );
          socket.close();
        });
      } else {
        C.log(
          `Download: No eligible sockets found to close (need open sockets with chunks being sent)`
        );
      }
    } else {
      C.log(
        `Download: Random value ${randomValue.toFixed(
          2
        )} did not meet threshold 0.1 - no sockets closed`
      );
    }
  };
}

export default WSAdvanceDownload;
